import { useEffect, useMemo, useState } from 'react';
import { Order, OrderType } from '../../../../clients/quipli-service/order/order';
import { ProductPrice, RentalProductPrice } from '../../../../clients/quipli-service/pricing/pricing';
import { toast } from '../../../../util/toasts';
import { usePricingApiV1 } from '../../../../clients/quipli-service/pricing/pricing-api-v1';

export const useProductPricing = (order: Order | undefined) => {
  const { listPrices } = usePricingApiV1();
  const [productPrices, setProductPrices] = useState<Map<string, ProductPrice[]>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const getProductPrices = async (order: Order) => {
    try {
      setIsLoading(true);
      const prodPrices: Map<string, ProductPrice[]> = new Map();

      for (const op of order.products) {
        const _prices = await listPrices({
          productIds: [op.product.id],
          locationId: order.locationId,
          orderId: order.id,
          userId: order.user.id,
          orderProductId: op.id,
        });

        const pricesWithOriginals = (_prices as RentalProductPrice[]).map((price) => ({
          ...price,
          originalPrice:
            price.type === OrderType.Rental && price.orderId
              ? (_prices.find(
                  (p) =>
                    p.type === OrderType.Rental &&
                    !p.orderId &&
                    p.durationValue === price.durationValue &&
                    p.durationUnit === price.durationUnit,
                ) as RentalProductPrice)
              : undefined,
        }));
        const overriddenIds = pricesWithOriginals.map((p) => p.originalPrice?.id).filter((id) => !!id) as string[];

        prodPrices.set(
          `${op.id}`,
          pricesWithOriginals
            .filter((p) => overriddenIds.indexOf(p.id) < 0 && (p.orderProductId == op.id || !p.orderProductId))
            .sort((a, b) => a.durationMinutes - b.durationMinutes),
        );
      }

      setProductPrices(prodPrices);
    } catch {
      toast('Unable to fetch product prices', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (order) {
      getProductPrices(order);
    }
  }, [order]);

  const rentalProductPricesAsList = useMemo(() => {
    return Array.from(productPrices.values())
      .flat()
      .filter((p): p is RentalProductPrice => p.type === OrderType.Rental && 'durationValue' in p);
  }, [productPrices]);

  return { productPrices, isLoading, rentalProductPricesAsList };
};
